<template>
  <WebLayout
    title="Planes y Precios"
    description="Descubre los planes y precios de Realgestión."
  >
    <div class="relative min-h-screen bg-warm-cream pb-20 overflow-hidden">
      <!-- Background decorations from Landing -->
      <div class="absolute inset-0 pointer-events-none z-0">
        <div class="absolute orb-1 rounded-full animate-float-slow" />
        <div class="absolute orb-2 rounded-full animate-float-slower" />
        <div class="absolute orb-3 rounded-full" />
        <div class="absolute inset-0 dot-pattern" />
      </div>

      <div class="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        <!-- Header Moderno -->
        <div class="text-center mb-12">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 text-orange-600 text-[10px] font-black uppercase tracking-widest mb-6 border border-orange-200">
            <i class="fas fa-rocket" /> Precios Transparentes
          </div>
          <h1 class="text-5xl font-black text-gray-900 tracking-tight mb-4">
            Un plan para cada etapa de <span class="text-orange-500">tu negocio</span>
          </h1>
          <p class="text-lg text-gray-400 font-medium max-w-2xl mx-auto">
            Comienza gratis y escala cuando lo necesites. Sin tarjetas de crédito, sin compromisos ocultos.
          </p>
        </div>

        <!-- Toggle Mensual/Trimestral/Anual -->
        <div class="flex justify-center mb-16 relative z-10">
          <div class="bg-gray-200/50 p-1.5 rounded-full flex items-center gap-1 shadow-inner relative flex-wrap justify-center">
            <button :class="['relative z-10 px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300', billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700']" @click="billingCycle = 'monthly'">
              Mensual
            </button>
            <button :class="['relative z-10 px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2', billingCycle === 'quarterly' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700']" @click="billingCycle = 'quarterly'">
              Trimestral <span class="bg-orange-100 text-orange-600 px-2 py-0.5 rounded-md text-[9px] -mr-2">-33%</span>
            </button>
            <button :class="['relative z-10 px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2', billingCycle === 'annual' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700']" @click="billingCycle = 'annual'">
              Anual <span class="bg-orange-100 text-orange-600 px-2 py-0.5 rounded-md text-[9px] -mr-2">-50%</span>
            </button>
          </div>
        </div>

        <!-- Pricing Bento Grid -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto">
          <div
            v-for="plan in plans" :key="plan.id"
            :class="[
              'rounded-3xl p-8 flex flex-col transition-all duration-500 group relative',
              plan.isPopular 
                ? 'bg-white border-2 border-orange-200 shadow-2xl shadow-orange-500/10 transform lg:-translate-y-4 hover:shadow-orange-500/20' 
                : 'bg-white border border-gray-100 shadow-sm hover:shadow-2xl hover:shadow-gray-200/50'
            ]"
          >
            <!-- Background Effects for Popular Plan -->
            <div v-if="plan.isPopular" class="absolute inset-0 bg-gradient-to-br from-orange-50/80 to-white rounded-3xl overflow-hidden pointer-events-none">
              <div class="absolute -right-10 -top-10 w-64 h-64 bg-orange-400/10 rounded-full blur-[60px] group-hover:scale-125 transition-transform duration-700" />
            </div>

            <!-- Popular Badge -->
            <div v-if="plan.isPopular" class="absolute top-4 right-6 z-20">
              <span class="px-3 py-1 rounded-full bg-orange-500 text-white text-[9px] font-black uppercase tracking-widest shadow-lg shadow-orange-500/30">
                Mejor Opción
              </span>
            </div>

            <div class="relative z-10 mb-8">
              <h3 :class="['text-2xl font-black mb-2 tracking-tight', plan.isPopular ? 'text-orange-900' : 'text-gray-900']">
                {{ plan.name }}
              </h3>
              <p :class="['text-[10px] font-black uppercase tracking-[0.2em] mb-6 min-h-[30px]', plan.isPopular ? 'text-orange-700/80' : 'text-gray-400']">
                {{ plan.description }}
              </p>
              <div class="flex items-baseline gap-2">
                <span v-if="plan.price !== null" :class="['text-5xl font-black tracking-tighter', plan.isPopular ? 'text-orange-600' : 'text-gray-900']">
                  ${{ getDisplayPrice(plan) }}
                </span>
                <span v-else :class="['text-4xl font-black tracking-tighter', plan.isPopular ? 'text-orange-600' : 'text-gray-900']">
                  A convenir
                </span>
                
                <span v-if="plan.price !== null" :class="['text-xs font-black uppercase tracking-widest flex flex-col', plan.isPopular ? 'text-orange-600/60' : 'text-gray-400']">
                  <span>/mes</span>
                  <span v-if="billingCycle !== 'monthly' && plan.price > 0" class="text-[9px] line-through">${{ plan.price }}</span>
                </span>
              </div>
              <div v-if="plan.price !== null && billingCycle !== 'monthly' && plan.price > 0" class="text-[10px] text-gray-500 font-medium mt-1">
                Pagas <strong class="text-orange-600">${{ billingCycle === 'annual' ? '180' : '60' }}</strong> {{ billingCycle === 'annual' ? 'al año' : 'cada 3 meses' }}
              </div>
              
              <!-- Pricing en Bs o Mensaje -->
              <div v-if="plan.price !== null" class="mt-2 flex items-center justify-between">
                <span v-if="plan.price > 0" class="text-xs font-bold" :class="plan.isPopular ? 'text-orange-700/60' : 'text-gray-500'">
                  Aprox. {{ getBsPrice(plan) }} Bs /mes
                </span>
                  
                <!-- Descuento indicator para el plan PRO -->
                <span v-if="plan.isPopular && billingCycle !== 'monthly'" class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-orange-500/10 text-orange-600 text-[10px] font-black uppercase tracking-widest border border-orange-500/20">
                  <i class="fas fa-check" /> Ahorras ${{ billingCycle === 'annual' ? '180' : '30' }}
                </span>
              </div>
            </div>
            
            <ul class="relative z-10 space-y-4 mb-10 flex-1">
              <li v-for="feat in plan.features" :key="feat" :class="['flex items-start gap-3 text-sm font-bold', plan.isPopular ? 'text-orange-900/80' : 'text-gray-600']">
                <div :class="['w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5', plan.isPopular ? 'bg-orange-500 shadow-md shadow-orange-500/30' : 'bg-gray-50 border border-gray-100']">
                  <i :class="['fas fa-check text-[9px]', plan.isPopular ? 'text-white' : 'text-gray-300']" />
                </div>
                <span class="leading-tight">{{ feat }}</span>
              </li>
            </ul>

            <Link
              v-if="plan.price !== null" href="/register" :class="[
                'relative z-10 w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-center transition-all active:scale-95 mt-auto',
                plan.isPopular 
                  ? 'bg-orange-500 text-white shadow-xl shadow-orange-500/30 hover:bg-orange-600 hover:shadow-orange-500/40' 
                  : 'bg-gray-100 text-gray-900 hover:bg-gray-200 hover:text-gray-900'
              ]"
            >
              {{ plan.price === 0 ? 'Comenzar Gratis' : 'Elegir ' + plan.name }}
            </Link>

            <a
              v-else href="https://wa.me/584244010195?text=Hola,%20me%20gustar%C3%ADa%20consultar%20por%20el%20plan%20Personalizado" target="_blank" :class="[
                'relative z-10 w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-center transition-all active:scale-95 mt-auto bg-gray-100 text-gray-900 hover:bg-gray-200 hover:text-gray-900'
              ]"
            >
              Contactar a Ventas
            </a>
          </div>
        </div>
        
        <div class="mt-20 text-center">
          <Link href="/" class="text-sm font-bold text-gray-500 hover:text-gray-900 underline decoration-2 underline-offset-4 transition-colors">
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { defineComponent } from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'

export default defineComponent({
  components: {
    Link,
    WebLayout,
  },
  props: {
    plans: Array,
    exchange_rate: Number,
  },
  data() {
    return {
      billingCycle: 'monthly',
    }
  },
  methods: {
    getDisplayPrice(plan) {
      if (plan.price === 0) return 0
      if (this.billingCycle === 'annual') return 15
      if (this.billingCycle === 'quarterly') return 20
      return plan.price
    },
    getBsPrice(plan) {
      if (plan.price === 0) return 0
      let price = plan.price
      if (this.billingCycle === 'annual') price = 15
      if (this.billingCycle === 'quarterly') price = 20
      return (price * this.exchange_rate).toFixed(2)
    },
  },
})
</script>

<style scoped>
/* ── TW2 Compatibility / Reused from Landing ── */
.bg-warm-cream { background-color: #FAFAF7; }

/* Background orbs */
.orb-1 { top: 10%; left: 5%; width: 500px; height: 500px; background: rgba(251, 191, 36, 0.12); filter: blur(120px); }
.orb-2 { bottom: 10%; right: 10%; width: 400px; height: 400px; background: rgba(245, 158, 11, 0.12); filter: blur(100px); }
.orb-3 { top: 40%; right: 30%; width: 300px; height: 300px; background: rgba(244, 63, 94, 0.06); filter: blur(80px); }

/* Dot patterns */
.dot-pattern {
  background-image: radial-gradient(circle, rgba(0,0,0,0.03) 1px, transparent 1px);
  background-size: 24px 24px;
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
}

@keyframes float-slow {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.05); }
}
@keyframes float-slower {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-15px) scale(0.95); }
}
.animate-float-slow {
  animation: float-slow 8s ease-in-out infinite;
}
.animate-float-slower {
  animation: float-slower 12s ease-in-out infinite;
}
</style>
